import { Component, OnDestroy, forwardRef } from '@angular/core';
import { ChangeDetectionStrategy, ChangeDetectorRef } from '@angular/core';
import { Input, Output, EventEmitter } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';

import { languageSupport, languageService } from '@unpispas/upp-base';

/**
 * Reusable language picker for reactive forms and standalone use.
 *
 * Renders a dropdown (`upp-select`) with flag icons and translated language names
 * (`@language_<code>`). The bound value is a single language code (for example `ES`, `EN`)
 * aligned with {@link languageService.GetLanguages}.
 *
 * @remarks
 * Extends {@link languageSupport} for `tr()` and view refresh when the app language changes.
 * Business rules (persist user locale, place default language, catalog `editLang`, etc.)
 * stay in the consuming feature; this widget only handles selection UI.
 *
 * @selector `upp-language-select`
 * @templateUrl `./upp-select.html`
 * @styleUrls `./upp-select.scss`
 * @providers `NG_VALUE_ACCESSOR`
 * @changeDetection `ChangeDetectionStrategy.OnPush`
 *
 * @example
 * ```html
 * <upp-language-select
 *     uppDataid="user-edit-language-select"
 *     formControlName="language"
 *     [title]="tr('@user_current_language')"
 *     (Changed)="OnLanguageApply($event)">
 * </upp-language-select>
 * ```
 */
@Component({
    selector: 'upp-language-select',
    templateUrl: './upp-select.html',
    styleUrls: ['./upp-select.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => UppLanguageSelectComponent),
            multi: true,
        },
    ],
})
export class UppLanguageSelectComponent extends languageSupport implements ControlValueAccessor, OnDestroy {
    /**
     * When true, the control cannot be changed by the user.
     * @defaultValue `false`
     */
    @Input() disabled = false;

    /**
     * Optional subtitle shown under the selected language name in the trigger.
     * The parent must pass already translated text; the widget does not infer copy by screen.
     * @defaultValue `''`
     */
    @Input() title = '';

    /**
     * Emits the selected language code when the user picks a different option.
     */
    @Output() Changed = new EventEmitter<string>();

    private _value = '';

    private _formDisabled = false;

    private _onChangeCallback: (v: string) => void = () => {
        // required by angular
    };

    private _onTouchCallback: () => void = () => {
        // required by angular
    };

    /**
     * @param lang Application language service (options and translations).
     * @param _cdr Change detector for OnPush updates.
     */
    constructor(private lang: languageService, private _cdr: ChangeDetectorRef) {
        super(lang, _cdr);
    }

    /**
     * Unsubscribes from language change notifications.
     */
    ngOnDestroy(): void {
        super.OnDestroy();
    }

    /** Current language code held by the control. */
    get value(): string {
        return this._value;
    }

    /** True when disabled via `@Input` or reactive form `setDisabledState`. */
    get IsDisabled(): boolean {
        return this.disabled || this._formDisabled;
    }

    /** Language codes available from {@link languageService.GetLanguages}. */
    get Languages(): string[] {
        return this._lang.GetLanguages();
    }

    /**
     * Stable identity for the language option list.
     * @param _index Row index (unused).
     * @param language Language code.
     */
    LanguageTrack(_index: number, language: string): string {
        return language;
    }

    /**
     * CSS classes for the flag icon of a language code.
     * @param language Language code (for example `ES`, `EN`).
     * @returns Flag icon class string, or a generic fallback when unknown.
     */
    LanguageFlag(language: string): string {
        const _flag = this._lang.FlagFor(language);
        if (!_flag) {
            return 'fi';
        }
        return 'fi fi-' + _flag.toLowerCase() + ' fis';
    }

    /**
     * Handles selection from the inner `upp-select`.
     * @param language Value from the selected option.
     */
    OnSelectChanged(language: unknown): void {
        const _next = language != null ? String(language) : '';
        if (_next === this._value) {
            return;
        }
        this._value = _next;
        this._onChangeCallback(_next);
        this._onTouchCallback();
        this.Changed.emit(_next);
        this._cdr.markForCheck();
    }

    /** @inheritdoc */
    writeValue(value: string | null): void {
        const _next = value != null ? String(value) : '';
        if (_next !== this._value) {
            this._value = _next;
            this._cdr.markForCheck();
        }
    }

    /** @inheritdoc */
    registerOnChange(fn: (v: string) => void): void {
        this._onChangeCallback = fn;
    }

    /** @inheritdoc */
    registerOnTouched(fn: () => void): void {
        this._onTouchCallback = fn;
    }

    /** @inheritdoc */
    setDisabledState(isDisabled: boolean): void {
        this._formDisabled = isDisabled;
        this._cdr.markForCheck();
    }
}
